import { useState } from "react";
import * as Popover from "@radix-ui/react-popover";
import type { Editor } from "@tiptap/react";
import { Baseline, Check, Highlighter } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { HIGHLIGHT_COLORS, TEXT_COLORS } from "./editor-colors";
import { ToolbarButton } from "./ToolbarButton";

type Mode = "color" | "highlight";

const COPY: Record<Mode, { label: string; custom: string; remove: string }> = {
  color: { label: "Yazı rengi", custom: "Özel renk", remove: "Rengi kaldır" },
  highlight: { label: "Vurgu rengi", custom: "Özel vurgu", remove: "Vurguyu kaldır" },
};

// Yazı rengi ya da vurgu (arka plan) rengi seçici: hazır palet + özel renk.
export function ColorPopover({
  editor,
  mode,
  current,
}: {
  editor: Editor;
  mode: Mode;
  // Seçimdeki mevcut renk (yoksa null).
  current: string | null;
}) {
  const [open, setOpen] = useState(false);
  const palette = mode === "color" ? TEXT_COLORS : HIGHLIGHT_COLORS;
  const copy = COPY[mode];

  // close:false → özel renk seçicide sürüklerken popover açık kalsın ve odak
  // editöre kaçıp tarayıcının renk penceresini kapatmasın (seçim state'te duruyor).
  const apply = (value: string, close = true) => {
    const chain = close ? editor.chain().focus() : editor.chain();
    if (mode === "color") chain.setColor(value).run();
    else chain.setHighlight({ color: value }).run();
    if (close) setOpen(false);
  };

  const remove = () => {
    const chain = editor.chain().focus();
    if (mode === "color") chain.unsetColor().run();
    else chain.unsetHighlight().run();
    setOpen(false);
  };

  const Icon = mode === "color" ? Baseline : Highlighter;
  const isCurrent = (value: string) => current?.toLowerCase() === value.toLowerCase();

  return (
    <Popover.Root open={open} onOpenChange={setOpen}>
      <Popover.Trigger asChild>
        <ToolbarButton label={copy.label} active={current !== null} className="flex-col gap-0 py-0.5">
          <Icon />
          {/* Mevcut rengi gösteren şerit */}
          <span
            aria-hidden
            className="h-0.5 w-3.5 rounded-full"
            style={{ backgroundColor: current ?? "transparent" }}
          />
        </ToolbarButton>
      </Popover.Trigger>
      <Popover.Portal>
        <Popover.Content
          align="start"
          sideOffset={4}
          onCloseAutoFocus={(e) => e.preventDefault()}
          className="z-50 w-56 space-y-2 rounded-md border border-border bg-popover p-2 text-popover-foreground shadow-md"
        >
          <p className="px-0.5 text-xs font-medium">{copy.label}</p>
          <div className="grid grid-cols-5 gap-1.5">
            {palette.map((c) => (
              <button
                key={c.value}
                type="button"
                title={c.label}
                aria-label={c.label}
                onClick={() => apply(c.value)}
                className="flex size-8 items-center justify-center rounded border border-border transition-transform hover:scale-110 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                style={{ backgroundColor: c.value }}
              >
                {isCurrent(c.value) && (
                  <Check className={cn("size-3.5", mode === "color" ? "text-white" : "text-foreground")} />
                )}
              </button>
            ))}
          </div>
          <label className="flex items-center justify-between gap-2 px-0.5 text-xs">
            {copy.custom}
            <input
              type="color"
              aria-label={copy.custom}
              value={current && /^#[0-9a-f]{6}$/i.test(current) ? current : "#000000"}
              onChange={(e) => apply(e.target.value, false)}
              className="h-7 w-10 cursor-pointer rounded border border-border bg-transparent p-0.5"
            />
          </label>
          <Button
            type="button"
            size="sm"
            variant="ghost"
            className="h-7 w-full text-xs"
            onClick={remove}
            disabled={current === null}
          >
            {copy.remove}
          </Button>
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
}
